import { lazy, Suspense } from 'react'
import { Route, Routes } from 'react-router-dom'
import { env } from '@/config/env'
import { AppShell } from '@/components/layout/AppShell'
import { Spinner } from '@/components/ui/spinner'
import { ForgotPasswordPage } from '@/features/auth/pages/ForgotPasswordPage'
import { LoginPage } from '@/features/auth/pages/LoginPage'
import { useAuthHub } from '@/features/auth/hooks/useAuthHub'
import { usePageViews } from '@/features/consent/usePageViews'
import { CookieConsentBanner } from '@/features/consent/CookieConsentBanner'
import { HomePage } from '@/features/home/HomePage'
import { InvoiceDetailPage } from '@/features/invoices/InvoiceDetailPage'
import { InvoicesPage } from '@/features/invoices/InvoicesPage'
import { InvoiceVociPage } from '@/features/invoices/InvoiceVociPage'
import { AutoletturaPage } from '@/features/autolettura/AutoletturaPage'
import { NotifichePage } from '@/features/notifiche/NotifichePage'
import { ProfilePage } from '@/features/profile/ProfilePage'
import { ContractDetailPage } from '@/features/profile/ContractDetailPage'
import { PrivacyPage } from './PrivacyPage'
import { NotFoundPage } from './NotFoundPage'
import { ProtectedRoute } from './ProtectedRoute'

// Code-splitting: queste pagine portano dipendenze pesanti che è inutile
// caricare per la maggior parte degli utenti.
//  - ConsumptionsPage: recharts (~200KB) + subscription Amplify
//  - BillPaymentPage: Stripe.js (~80KB) + lambda client
//  - PaymentReturnPage: idem stripe
//  - SecuritySettingsPage: MFA Cognito helpers
//  - News*: dompurify (~50KB)
const ConsumptionsPage = lazy(() =>
  import('@/features/consumptions/ConsumptionsPage').then((m) => ({
    default: m.ConsumptionsPage,
  })),
)
const BillPaymentPage = lazy(() =>
  import('@/features/payments/BillPaymentPage').then((m) => ({
    default: m.BillPaymentPage,
  })),
)
const PaymentReturnPage = lazy(() =>
  import('@/features/payments/PaymentReturnPage').then((m) => ({
    default: m.PaymentReturnPage,
  })),
)
const SecuritySettingsPage = lazy(() =>
  import('@/features/profile/SecuritySettingsPage').then((m) => ({
    default: m.SecuritySettingsPage,
  })),
)
const NewsListPage = lazy(() =>
  import('@/features/news/NewsListPage').then((m) => ({ default: m.NewsListPage })),
)
const NewsDetailPage = lazy(() =>
  import('@/features/news/NewsDetailPage').then((m) => ({ default: m.NewsDetailPage })),
)
// Pagina pubblica (non protetta) per il flusso preventivo:
// drop bolletta -> OCR DocuPipe -> validazione -> confronto -> contratto.
// Bundle a sé per non gravare sul caricamento dell'area cliente.
const PreventivoPage = lazy(() =>
  import('@/features/preventivo/pages/PreventivoPage').then((m) => ({
    default: m.PreventivoPage,
  })),
)

function RouteFallback() {
  return (
    <div className="flex min-h-[50vh] items-center justify-center">
      <Spinner />
    </div>
  )
}

export function AppRoutes() {
  // Globale: ascolta `signedOut` / `tokenRefresh_failure` e ridirige a /login
  useAuthHub()
  // Globale: invia un page_view a GA a ogni cambio rotta (no-op senza consenso).
  usePageViews()

  return (
    <>
      <Routes>
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        {/* Pagina pubblica /preventivo: attiva solo con VITE_PREVENTIVO_ENABLED=true.
          Finché è disabilitata mostra una 404 (come se la rotta non esistesse). */}
        <Route
          path="/preventivo"
          element={
            env.preventivo.enabled ? (
              <Suspense fallback={<RouteFallback />}>
                <PreventivoPage />
              </Suspense>
            ) : (
              <NotFoundPage />
            )
          }
        />

        <Route
          element={
            <ProtectedRoute>
              <AppShell />
            </ProtectedRoute>
          }
        >
          <Route index element={<HomePage />} />
          <Route path="bollette" element={<InvoicesPage />} />
          <Route path="bollette/:codice/voci" element={<InvoiceVociPage />} />
          <Route path="bollette/:codice" element={<InvoiceDetailPage />} />
          <Route
            path="pagamenti/return"
            element={
              <Suspense fallback={<RouteFallback />}>
                <PaymentReturnPage />
              </Suspense>
            }
          />
          <Route
            path="pagamenti/:codice"
            element={
              <Suspense fallback={<RouteFallback />}>
                <BillPaymentPage />
              </Suspense>
            }
          />
          <Route
            path="news"
            element={
              <Suspense fallback={<RouteFallback />}>
                <NewsListPage />
              </Suspense>
            }
          />
          <Route
            path="news/:newsKey"
            element={
              <Suspense fallback={<RouteFallback />}>
                <NewsDetailPage />
              </Suspense>
            }
          />
          <Route path="autolettura" element={<AutoletturaPage />} />
          <Route path="notifiche" element={<NotifichePage />} />
          <Route
            path="consumi"
            element={
              <Suspense fallback={<RouteFallback />}>
                <ConsumptionsPage />
              </Suspense>
            }
          />
          <Route path="profilo" element={<ProfilePage />} />
          <Route path="profilo/contratto/:codice" element={<ContractDetailPage />} />
          <Route
            path="profilo/sicurezza"
            element={
              <Suspense fallback={<RouteFallback />}>
                <SecuritySettingsPage />
              </Suspense>
            }
          />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
      <CookieConsentBanner />
    </>
  )
}
